import React, { useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import {
  Button,
  type ButtonProps,
  Modal,
  Row,
  Col,
  Image,
  Checkbox,
  Tree,
  Input,
  Dropdown,
  Select,
  Space,
  message,
  Tooltip,
  Divider,
  Empty,
  MenuProps,
  Typography,
  Badge,
  DatePicker,
  Spin,
  Tag,
  Grid,
} from 'antd';
import {
  FolderOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MoreOutlined,
  MenuOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  DragOutlined,
  FileOutlined,
  FileTextOutlined,
  StarOutlined,
  EyeOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { conns } from '@cfg/axiosConfig.tsx';
import { ImageInfo, ImageFolder, MediaKind } from '@app-types/commons/images.types.ts';
import { getHost } from '@tools/conns/PathHandler.tsx';
import { CustomUploadFile } from '@tools/types/commonFields.types.ts';
import { UploadDragerSorteable } from '@tools/components/commonFields.tsx';
import { logInfo, logError } from '@tools/LogsHandler.tsx';
import { Dayjs } from 'dayjs';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteSettings, type SiteSettings } from '@store/slices/settingsSlice.tsx';
import { buildImageUploadFormData } from './utils/buildImageUploadFormData.ts';
import { canAddSelection, limitSelection } from './imageSelection';
import './ImageManager.css';

// Types
interface ComponentOptions {
  /** Carpeta inicial para abrir el modal; si no se envía, abre "Todas las imágenes" */
  initialFolderId?: number | null;
  /** Nombre de la carpeta por defecto; se crea si no existe */
  defaultFolderName?: string;
  /** Sobrescribe el siteCode a utilizar al listar media */
  siteCode?: string;
  /** Define el tipo de media inicial a filtrar */
  defaultKind?: MediaKind;
}

type SelectedSize = 'sm' | 'md' | 'lg' | { w: number; h: number };

interface Props {
  value?: ImageInfo[];
  onChange: (images: ImageInfo[]) => void;
  /** Emite la imagen confirmada en modo single-select */
  onSelect?: (image: ImageInfo | null) => void;
  /** Opciones del componente (ej. carpeta inicial) */
  options?: ComponentOptions;
  /** Permitir selección múltiple (true por defecto) */
  multiple?: boolean;
  /** Tamaño de previsualización cuando el modo es single-select */
  selectedSize?: SelectedSize;
  /** Mostrar previsualización de imágenes seleccionadas (true por defecto) */
  showPreview?: boolean;
  /** Texto del botón disparador del gestor */
  triggerText?: string;
  /** Icono opcional para el boton disparador */
  triggerIcon?: ReactNode;
  /** Tipo visual del boton disparador */
  triggerType?: ButtonProps['type'];
  /** Tamano del boton disparador */
  triggerSize?: ButtonProps['size'];
  /** Etiqueta accesible del boton disparador */
  triggerAriaLabel?: string;
  /** Limite opcional de imagenes seleccionables */
  maxSelection?: number;
  /** Reordena en vivo la grilla del modal para llevar arriba las seleccionadas */
  prioritizeSelectedOnInteract?: boolean;
}

interface TreeNodeData {
  title: string;
  key: string;
  value: number | null;
  icon: React.ReactNode;
  children: TreeNodeData[];
  folder?: ImageFolder;
  draggable: boolean;
}

interface ImageState {
  library: ImageInfo[];
  filteredLibrary: ImageInfo[];
  folders: ImageFolder[];
  selected: Set<string | number>; // Para selección de biblioteca
  organizeSelected: Set<string | number>; // Para selección de organización
  selectedFolder: number | null;
  searchTerm: string;
  dragOverFolder: number | null;
  organizeMode: boolean;
  isLoading: boolean;
  nextCursor: string | null;
  hasMore: boolean;
  includeDescendants: boolean;
}

interface CaptionEditorState {
  image: ImageInfo;
  value: string;
}

// Constants
const DRAG_DATA_TYPES = {
  IMAGE: 'image:',
  IMAGES: 'images:',
} as const;

const FOLDER_KEYS = {
  ALL: 'all',
} as const;

interface MediaFilters {
  kind: MediaKind;
  from: Dayjs | null;
  to: Dayjs | null;
}

interface MediaListItem {
  id?: number | string | null;
  url?: string | null;
  legacy_url?: string | null;
  thumbnails?: ImageInfo['thumbnails'];
  caption?: string | null;
  folder_id?: number | null;
  created_at?: string | null;
  kind?: MediaKind;
  file_path_large?: string | null;
  file_path_medium?: string | null;
  file_path_small?: string | null;
}

interface MediaListResponse {
  items?: MediaListItem[];
  nextCursor?: string | null;
  pageSize?: number | null;
}

const MEDIA_PAGE_SIZE = 20;
const MEDIA_LIBRARY_V2_FLAG = import.meta.env.VITE_MEDIA_LIBRARY_V2 === 'true';
const { RangePicker } = DatePicker;

export interface BuildMediaListParamsOptions {
  filters: MediaFilters;
  folderId: number | null;
  includeDescendants: boolean;
  cursor: string | null;
  searchTerm: string;
  pageSize?: number;
}

export const normalizeMediaSiteArgs = (siteCode: string | null | undefined): string[] => {
  const trimmed = siteCode?.trim();
  return trimmed ? [trimmed] : [];
};

export const buildMediaListParams = ({
  filters,
  folderId,
  includeDescendants,
  cursor,
  searchTerm,
  pageSize = MEDIA_PAGE_SIZE,
}: BuildMediaListParamsOptions): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    kind: filters.kind,
    pageSize,
  };

  if (cursor) params.cursor = cursor;
  if (folderId !== null) params.folder_id = folderId;
  if (includeDescendants) params.include_descendants = true;

  const trimmedSearch = searchTerm.trim();
  if (trimmedSearch) {
    params.q = trimmedSearch;
  }

  const fromParam = formatDateParam(filters.from, 'start');
  if (fromParam) params.from = fromParam;
  const toParam = formatDateParam(filters.to, 'end');
  if (toParam) params.to = toParam;

  return params;
};

const makeMediaKey = (item: ImageInfo): string | null => {
  if (item.id != null) {
    return `id:${item.id}`;
  }
  if (item.file_path_large) {
    return `url:${item.file_path_large}`;
  }
  if (item.file_path_medium) {
    return `url:${item.file_path_medium}`;
  }
  if (item.file_path_small) {
    return `url:${item.file_path_small}`;
  }
  return null;
};

export const mergeMediaLists = (existing: ImageInfo[], incoming: ImageInfo[]): ImageInfo[] => {
  const result: ImageInfo[] = [];
  const indexByKey = new Map<string, number>();

  existing.forEach(item => {
    const key = makeMediaKey(item);
    if (key) {
      indexByKey.set(key, result.length);
    }
    result.push(item);
  });

  incoming.forEach(item => {
    const key = makeMediaKey(item);
    if (key && indexByKey.has(key)) {
      const idx = indexByKey.get(key)!;
      result[idx] = { ...result[idx], ...item };
    } else {
      if (key) {
        indexByKey.set(key, result.length);
      }
      result.push(item);
    }
  });

  return result;
};

const isImageKind = (kind?: MediaKind): boolean => !kind || kind === 'images';

const extractFileName = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  const withoutQuery = path.split('?')[0] ?? path;
  const segments = withoutQuery.split('/');
  const candidate = segments[segments.length - 1];
  return candidate && candidate.trim().length > 0 ? candidate : undefined;
};

const shortenLabel = (value: string, max = 36): string => {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 1)}…`;
};

const getMediaLabel = (img: ImageInfo, id: string | number): string => {
  return (
    img.caption ||
    img.alt ||
    extractFileName(img.file_path_large) ||
    (typeof id === 'number' ? `Media ${id}` : 'Archivo sin descripción')
  );
};

const resolveAltText = (img: ImageInfo, fallback: string): string => {
  return img.alt?.trim() || img.caption?.trim() || fallback;
};

const kindIconMap: Record<Exclude<MediaKind, 'images'>, React.ReactNode> = {
  docs: <FileTextOutlined style={{ fontSize: 32 }} />,
  favicon: <StarOutlined style={{ fontSize: 32 }} />,
  other: <FileOutlined style={{ fontSize: 32 }} />,
};

const renderNonImagePreview = (
  img: ImageInfo,
  label: string,
  variant: 'grid' | 'preview',
): React.ReactNode => {
  const kind = img.kind ?? 'other';
  const icon = kindIconMap[kind as Exclude<MediaKind, 'images'>] ?? kindIconMap.other;
  const isPreview = variant === 'preview';

  return (
    <div
      data-testid={`media-kind-${kind}${isPreview ? '-preview' : ''}`}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isPreview ? 24 : 12,
        gap: 8,
        backgroundColor: '#fafafa',
        color: '#555',
      }}
      aria-label={`${label} (${kind})`}
    >
      {icon}
      <Typography.Text style={{ textAlign: 'center', width: '100%' }} className="text-ellipsis-1">
        {shortenLabel(label, isPreview ? 48 : 28)}
      </Typography.Text>
    </div>
  );
};

const adaptMediaItemToImageInfo = (item: MediaListItem): ImageInfo => {
  const resolvedKind: MediaKind = item.kind ?? 'images';
  let parsedId: number | undefined;
  if (typeof item.id === 'number') {
    parsedId = item.id;
  } else if (typeof item.id === 'string') {
    const numeric = Number.parseInt(item.id, 10);
    if (!Number.isNaN(numeric)) {
      parsedId = numeric;
    }
  }

  const canonical =
    item.url ??
    item.file_path_large ??
    item.file_path_medium ??
    item.file_path_small ??
    item.legacy_url ??
    '';

  return {
    id: parsedId,
    file_path_large: item.file_path_large ?? canonical,
    file_path_medium: item.file_path_medium ?? undefined,
    file_path_small: item.file_path_small ?? undefined,
    thumbnails: item.thumbnails,
    caption: item.caption ?? undefined,
    folder_id: item.folder_id ?? undefined,
    created_at: item.created_at ?? undefined,
    kind: resolvedKind,
  } as ImageInfo;
};

const formatDateParam = (value: Dayjs | null, boundary: 'start' | 'end'): string | undefined => {
  if (!value) return undefined;
  const target = boundary === 'start' ? value.startOf('day') : value.endOf('day');
  return target.toISOString();
};

// Utility functions
const fileToInfo = (f: CustomUploadFile): ImageInfo => {
  const url = f.url || (f.originFileObj ? URL.createObjectURL(f.originFileObj as Blob) : '');
  return {
    file_path_large: url,
    thumbnails: { urls: { small: url, medium: url, large: url }, default: url },
    caption: f.caption,
    order: f.order,
  } as ImageInfo;
};

// Solo considerar http(s)://, //, data:, blob: como absolutas web
const isWebAbsoluteUrl = (url?: string): boolean => {
  if (!url) return false;
  return /^(?:https?:)?\/\//i.test(url) || /^data:/i.test(url) || /^blob:/i.test(url);
};

// Normaliza cualquier path 'file://' o 'C:\...\media\...' a '/media/...'
const toRelativeMediaPath = (p: string): string => {
  if (!p) return '';
  let s = p.replace(/^file:\/\//i, '').replace(/\\/g, '/');

  // Busca el segmento /media/
  const idx = s.toLowerCase().lastIndexOf('/media/');
  if (idx >= 0) {
    s = s.slice(idx);              // => '/media/...'
  }
  // Asegura prefijo único
  s = '/' + s.replace(/^\/+/, '');
  return s;
};

export const getApiBaseUrl = (): string => {
  // const raw = (import.meta.env?.VITE_API_URL ?? '') as string;
  const raw = getHost() as string;
  return raw ? raw.replace(/\/+$/, '') : ''; // sin trailing slash
};

// AVIF support detection with caching
let avifSupport: boolean | null = null;
const supportsAvif = (): boolean => {
  if (avifSupport !== null) return avifSupport;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    avifSupport = canvas.toDataURL('image/avif').startsWith('data:image/avif');
  } catch {
    avifSupport = false;
  }
  return avifSupport;
};

export const getThumbUrl = (img: ImageInfo, size: 'small' | 'medium' | 'large' = 'small'): string => {
  const raw =
    img?.thumbnails?.urls?.[size] ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (size !== 'large' ? (img as any)[`file_path_${size}`] : undefined) ??
    img?.file_path_large ??
    '';
  if (!raw) return '';

  // 1) ¿Ya es web absoluta? (http/https//, data:, blob:)
  if (isWebAbsoluteUrl(raw)) return raw;

  let url = toRelativeMediaPath(raw);

  if (!supportsAvif() && !/format=/.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    url += `${sep}format=webp`;
  }

  const base = getApiBaseUrl();
  const clean = url.replace(/^\/+/, '');
  return base ? `${base}/${clean}` : `/${clean}`;
};

/**
 * Normaliza la colección de carpetas para evitar nodos duplicados cuando la API
 * devuelve múltiples filas con el mismo identificador.
 */
const normalizeFolders = (folders: ImageFolder[]): ImageFolder[] => {
  const uniqueById = new Map<number, ImageFolder>();

  folders.forEach((folder) => {
    uniqueById.set(folder.id, folder);
  });

  return Array.from(uniqueById.values());
};

// Enhanced folder tree builder with proper typing
const buildFolderTree = (folders: ImageFolder[]): TreeNodeData[] => {
  const normalizedFolders = normalizeFolders(folders);
  const folderMap = new Map<number, TreeNodeData>();
  const rootFolders: TreeNodeData[] = [];
  const attachedFolderIds = new Set<number>();

  // Crear nodos del mapa
  normalizedFolders.forEach(folder => {
    folderMap.set(folder.id, {
      title: folder.name,
      key: folder.id.toString(),
      value: folder.id,
      icon: <FolderOutlined />,
      children: [],
      folder: folder,
      draggable: true, // Enable dragging for folders
    });
  });

  // Construir jerarquía
  normalizedFolders.forEach(folder => {
    const node = folderMap.get(folder.id);
    if (!node || attachedFolderIds.has(folder.id)) {
      return;
    }

    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      folderMap.get(folder.parent_id)!.children.push(node);
      attachedFolderIds.add(folder.id);
    } else {
      rootFolders.push(node);
      attachedFolderIds.add(folder.id);
    }
  });

  return [
    {
      title: 'Todas las imágenes',
      key: FOLDER_KEYS.ALL,
      value: null,
      icon: <FolderOpenOutlined />,
      children: [],
      draggable: false, // Root folder should not be draggable
    },
    ...rootFolders
  ];
};

const PREVIEW_SIZE_MAP: Record<'sm' | 'md' | 'lg', { w: number; h: number }> = {
  sm: { w: 120, h: 120 },
  md: { w: 200, h: 200 },
  lg: { w: 320, h: 320 },
};

const SELECTED_PREVIEW_PAGE_SIZE = 8;

/**
 * Gestiona selección, subida y organización de media reutilizable.
 * Permite desacoplar el reordenamiento de seleccionadas durante la interacción
 * sin perder la priorización al reabrir el modal.
 */
const ImageManager: React.FC<Props> = ({
  value = [],
  onChange,
  onSelect,
  options,
  multiple = true,
  selectedSize = 'md',
  showPreview = true,
  triggerText = 'Seleccionar media',
  triggerIcon,
  triggerType,
  triggerSize = 'small',
  triggerAriaLabel,
  maxSelection,
  prioritizeSelectedOnInteract = false,
}) => {
  const siteSettings = useAppSelector(selectSiteSettings);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const resolvedMode: 'single' | 'multiple' = multiple ? 'multiple' : 'single';
  const previewDimensions = typeof selectedSize === 'string'
    ? PREVIEW_SIZE_MAP[selectedSize] ?? PREVIEW_SIZE_MAP.md
    : {
      w: selectedSize.w ?? PREVIEW_SIZE_MAP.md.w,
      h: selectedSize.h ?? PREVIEW_SIZE_MAP.md.h,
    };

  // State management
  const initialImages = useMemo(
    () => (resolvedMode === 'single' ? value.slice(0, 1) : value),
    [resolvedMode, value],
  );
  const [images, setImages] = useState<ImageInfo[]>(initialImages);
  const [libVisible, setLibVisible] = useState(false);
  const [tempFiles, setTempFiles] = useState<CustomUploadFile[]>([]);
  const [uploadSessionKey, setUploadSessionKey] = useState(0);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ImageFolder | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [captionEditorOpen, setCaptionEditorOpen] = useState<Set<string>>(new Set());
  const [captionEditorDrafts, setCaptionEditorDrafts] = useState<Record<string, string>>({});
  const [selectedPreviewPage, setSelectedPreviewPage] = useState(1);
  const [selectionPriorityIds, setSelectionPriorityIds] = useState<Array<string | number>>([]);
  const [visibleLibraryCount, setVisibleLibraryCount] = useState(MEDIA_PAGE_SIZE);
  const [previewImage, setPreviewImage] = useState<ImageInfo | null>(null);
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);

  const tempFileMap = useMemo(() => {
    const entries = new Map<string, CustomUploadFile>();
    tempFiles.forEach((file) => {
      entries.set(file.uid, file);
    });
    return entries;
  }, [tempFiles]);

  const handleTempCaptionChange = useCallback((uid: string, caption: string) => {
    setTempFiles((prev) => {
      let changed = false;
      const next = prev.map((file) => {
        if (file.uid !== uid) return file;
        if (file.caption === caption) return file;
        changed = true;
        return { ...file, caption };
      });
      return changed ? next : prev;
    });
  }, []);

  const getImageKey = useCallback((img: ImageInfo, index: number): string => {
    return String(
      img.id ??
      img.file_path_large ??
      img.file_path_medium ??
      img.file_path_small ??
      index,
    );
  }, []);

  const buildSelectionPriorityIds = useCallback(
    (baseImages: ImageInfo[], tempSelection: CustomUploadFile[] = []): Array<string | number> => {
      const persistentIds = baseImages
        .map((img) => img.id)
        .filter((id): id is number => id !== undefined);

      const uploadIds = tempSelection.map((file) => file.uid);
      const ids = resolvedMode === 'multiple'
        ? [...persistentIds, ...uploadIds]
        : [...persistentIds, ...uploadIds].slice(0, 1);

      return ids;
    },
    [resolvedMode],
  );

  const openCaptionEditor = useCallback((key: string, currentCaption?: string) => {
    setCaptionEditorOpen((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setCaptionEditorDrafts((prev) => ({
      ...prev,
      [key]: currentCaption ?? '',
    }));
  }, []);

  const closeCaptionEditor = useCallback((key: string) => {
    setCaptionEditorOpen((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setCaptionEditorDrafts((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleCaptionDraftChange = useCallback((key: string, value: string) => {
    setCaptionEditorDrafts((prev) => ({
      ...prev,
      [key]: value.slice(0, 125),
    }));
  }, []);

  const handleCaptionChange = useCallback((key: string, value: string) => {
    const trimmedValue = value.slice(0, 125);
    const nextCaption = trimmedValue.trim().length ? trimmedValue : undefined;

    setImages((prev) => {
      let changed = false;
      const next = prev.map((img, index) => {
        const currentKey = getImageKey(img, index);
        if (currentKey !== key) {
          return img;
        }
        if (img.caption === nextCaption) {
          return img;
        }
        changed = true;
        return { ...img, caption: nextCaption };
      });

      if (!changed) {
        return prev;
      }

      onChange(next);
      if (resolvedMode === 'single') {
        onSelect?.(next[0] ?? null);
      }
      return next;
    });
  }, [getImageKey, onChange, onSelect, resolvedMode]);

  const confirmCaptionChange = useCallback((key: string) => {
    handleCaptionChange(key, captionEditorDrafts[key] ?? '');
    closeCaptionEditor(key);
  }, [captionEditorDrafts, closeCaptionEditor, handleCaptionChange]);

  // Image and folder state
  const [imageState, setImageState] = useState<ImageState>({
    library: [],
    filteredLibrary: [],
    folders: [],
    selected: new Set(), // Selección para biblioteca
    organizeSelected: new Set(), // Selección para organización
    selectedFolder: null,
    searchTerm: '',
    dragOverFolder: null,
    organizeMode: false,
    isLoading: false,
    nextCursor: null,
    hasMore: false,
    includeDescendants: false,
  });

  const [mediaFilters, setMediaFilters] = useState<MediaFilters>({
    kind: options?.defaultKind ?? 'images',
    from: null,
    to: null,
  });
  const [captionEditor, setCaptionEditor] = useState<CaptionEditorState | null>(null);
  const [savingCaption, setSavingCaption] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    visible: boolean;
    imageIds: number[];
    fkCount: number;
    fkImageCount: number;
    isDeleting: boolean;
  }>({ visible: false, imageIds: [], fkCount: 0, fkImageCount: 0, isDeleting: false });

  useEffect(() => {
    const nextKind = options?.defaultKind ?? 'images';
    setMediaFilters(prev => {
      if (prev.kind === nextKind) {
        return prev;
      }
      return { ...prev, kind: nextKind };
    });
  }, [options?.defaultKind]);

  useEffect(() => {
    if (resolvedMode === 'single' && imageState.organizeMode) {
      setImageState(prev => ({
        ...prev,
        organizeMode: false,
        organizeSelected: new Set(),
      }));
    }
  }, [imageState.organizeMode, resolvedMode]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(images.length / SELECTED_PREVIEW_PAGE_SIZE));
    setSelectedPreviewPage((prev) => Math.min(prev, maxPage));
  }, [images.length]);

  // Refs for cleanup
  const blobUrls = useRef<Set<string>>(new Set());
  const searchDebounceRef = useRef<number>(0);
  const previousSearchTermRef = useRef<string>('');
  const draggingImageRef = useRef(false);

  const resolvedSiteCode = useMemo(() => {
    const siteFromSettings =
      (siteSettings as (SiteSettings & { site_code?: string | null; code?: string | null }) | null)?.site_code ??
      (siteSettings as (SiteSettings & { code?: string | null }) | null)?.code ??
      undefined;

    const siteFromWindow = (() => {
      if (typeof window === 'undefined') return undefined;
      const candidate = window as typeof window & { __SITE_CODE__?: string; SITE_CODE?: string };
      return candidate.__SITE_CODE__ ?? candidate.SITE_CODE ?? undefined;
    })();

    const fallback = options?.defaultFolderName;
    const candidate = options?.siteCode ?? siteFromSettings ?? siteFromWindow ?? fallback;
    const trimmed = candidate?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : 'web';
  }, [options?.siteCode, options?.defaultFolderName, siteSettings]);

  const useMediaV2 = useMemo(() => MEDIA_LIBRARY_V2_FLAG && Boolean(resolvedSiteCode), [resolvedSiteCode]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrls.current.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const stopParentSubmitOnEnter: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key !== 'Enter') return;

    const el = e.target as HTMLElement | null;
    if (!el) return;

    const tag = el.tagName; // 'INPUT', 'TEXTAREA', 'BUTTON', etc.
    const role = el.getAttribute('role');

    // Permitimos Enter en campos de texto real (textarea o widgets con role textbox)
    const isTextual =
      tag === 'TEXTAREA' ||
      role === 'textbox' ||
      // si usás AntD Input/Select, cubrimos contenteditable
      el.getAttribute('contenteditable') === 'true';

    if (!isTextual) {
      // Evita que el <form> ancestro reciba submit
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const stopSubmitCapture: React.FormEventHandler<HTMLDivElement> = (e) => {
    // defensa extra por si algún lib dispara un submit de evento custom
    e.stopPropagation();
  };

  // API functions with better error handling
  const handleApiError = useCallback((error: unknown, fallbackMessage: string) => {
    logError('API Error:', error);
    message.error(fallbackMessage);
  }, []);

  const loadFolders = useCallback(async (): Promise<ImageFolder[]> => {
    try {
      const res = await conns<ImageFolder[]>('eshop:dashboard:image:folder:list').get();
      const normalizedFolders = normalizeFolders(res.data);
      setImageState(prev => ({ ...prev, folders: normalizedFolders }));
      return normalizedFolders;
    } catch (error) {
      handleApiError(error, 'Error al cargar carpetas');
      return [];
    }
  }, [handleApiError]);

  const fetchMediaList = useCallback(
    async ({
      reset,
      folderId,
      cursor,
      includeDescendants,
      filters,
      searchTerm,
    }: {
      reset: boolean;
      folderId: number | null;
      cursor: string | null;
      includeDescendants: boolean;
      filters: MediaFilters;
      searchTerm: string;
    }): Promise<void> => {
      if (!useMediaV2) {
        return;
      }
      const siteArgs = normalizeMediaSiteArgs(resolvedSiteCode);
      if (siteArgs.length === 0) {
        handleApiError(new Error('siteCode-missing'), 'No se pudo determinar el sitio actual');
        return;
      }

      setImageState(prev => ({
        ...prev,
        isLoading: true,
        includeDescendants,
        ...(reset
          ? {
            library: [],
            filteredLibrary: [],
            nextCursor: null,
            hasMore: false,
          }
          : {}),
      }));

      const params = buildMediaListParams({
        filters,
        folderId,
        includeDescendants,
        cursor,
        searchTerm,
        pageSize: MEDIA_PAGE_SIZE,
      });

      try {
        const res = await conns<MediaListResponse>('media:list', siteArgs).get(params);
        const payload = res.data ?? {};
        const mapped = (payload.items ?? []).map(adaptMediaItemToImageInfo);

        setImageState(prev => {
          const base = reset ? [] : prev.library;
          const merged = reset ? mapped : mergeMediaLists(base, mapped);
          return {
            ...prev,
            library: merged,
            filteredLibrary: merged,
            isLoading: false,
            nextCursor: payload.nextCursor ?? null,
            hasMore: Boolean(payload.nextCursor),
            includeDescendants,
          };
        });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          logInfo('Media library not found for requested site', { siteCode: resolvedSiteCode });
          message.warning('No encontramos media para este sitio');
          setImageState(prev => ({
            ...prev,
            library: [],
            filteredLibrary: [],
            isLoading: false,
            nextCursor: null,
            hasMore: false,
            includeDescendants,
          }));
          return;
        }
        handleApiError(error, 'Error al cargar imágenes');
        setImageState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [useMediaV2, resolvedSiteCode, handleApiError],
  );

  // Cargar biblioteca de imágenes
  const loadLibrary = useCallback(async (folderId: number | null = null): Promise<void> => {
    if (useMediaV2) {
      await fetchMediaList({
        reset: true,
        folderId,
        cursor: null,
        includeDescendants: false,
        filters: mediaFilters,
        searchTerm: imageState.searchTerm,
      });
      return;
    }

    try {
      const params = folderId !== null ? { folder_id: folderId } : {};
      const res = await conns<ImageInfo[]>('eshop:dashboard:image:list').get(params);
      setImageState(prev => ({
        ...prev,
        library: res.data,
        filteredLibrary: res.data,
      }));
    } catch (error) {
      handleApiError(error, 'Error al cargar imágenes');
    }
  }, [fetchMediaList, useMediaV2, mediaFilters, imageState.searchTerm, handleApiError]);

  // Memoized descendant folder calculation
  const getDescendantFolderIds = useCallback((folderId: number): number[] => {
    const ids: number[] = [folderId];
    const childrenMap = new Map<number, number[]>();

    imageState.folders.forEach(f => {
      if (f.parent_id != null) {
        if (!childrenMap.has(f.parent_id)) childrenMap.set(f.parent_id, []);
        childrenMap.get(f.parent_id)!.push(f.id);
      }
    });

    const stack: number[] = [...(childrenMap.get(folderId) || [])];
    ids.push(...stack);

    while (stack.length) {
      const cur = stack.pop()!;
      const kids = childrenMap.get(cur) || [];
      ids.push(...kids);
      stack.push(...kids);
    }

    return Array.from(new Set(ids));
  }, [imageState.folders]);

  // Cargar imágenes de una carpeta incluyendo sus subcarpetas
  const loadLibraryRecursive = useCallback(async (folderId: number | null): Promise<void> => {
    if (folderId === null) {
      return loadLibrary(null);
    }

    if (useMediaV2) {
      await fetchMediaList({
        reset: true,
        folderId,
        cursor: null,
        includeDescendants: true,
        filters: mediaFilters,
        searchTerm: imageState.searchTerm,
      });
      return;
    }

    try {
      const ids = getDescendantFolderIds(folderId);
      const results = await Promise.all(
        ids.map(id => conns<ImageInfo[]>('eshop:dashboard:image:list').get({ folder_id: id }))
      );

      const combined: Record<number, ImageInfo> = {};
      results.forEach((res: { data: ImageInfo[] | undefined }) => {
        (res.data ?? []).forEach((img: ImageInfo) => {
          if (img.id != null) combined[img.id] = img;
        });
      });

      const list = Object.values(combined);
      setImageState(prev => ({
        ...prev,
        library: list,
        filteredLibrary: filterImages(prev.searchTerm, list),
      }));
    } catch (error) {
      handleApiError(error, 'Error al cargar imágenes recursivamente');
    }
  }, [getDescendantFolderIds, loadLibrary, fetchMediaList, useMediaV2, mediaFilters, imageState.searchTerm, handleApiError]);

  const loadMore = useCallback(async () => {
    if (!useMediaV2) {
      setVisibleLibraryCount(prev => prev + MEDIA_PAGE_SIZE);
      return;
    }

    if (!imageState.hasMore || !imageState.nextCursor) {
      return;
    }

    await fetchMediaList({
      reset: false,
      folderId: imageState.selectedFolder,
      cursor: imageState.nextCursor,
      includeDescendants: imageState.includeDescendants,
      filters: mediaFilters,
      searchTerm: imageState.searchTerm,
    });
  }, [useMediaV2, imageState.hasMore, imageState.nextCursor, imageState.selectedFolder, imageState.includeDescendants, mediaFilters, imageState.searchTerm, fetchMediaList]);

  // Optimized filter function with memoization
  const filterImages = useCallback((searchTerm: string, imageList: ImageInfo[]): ImageInfo[] => {
    if (!searchTerm.trim()) {
      return imageList;
    }

    const term = searchTerm.toLowerCase();
    return imageList.filter(img =>
      img.caption?.toLowerCase().includes(term) ||
      img.file_path_large?.toLowerCase().includes(term)
    );
  }, []);

  const handleKindChange = useCallback((value: MediaKind) => {
    setMediaFilters(prev => ({ ...prev, kind: value }));
  }, []);

  const handleDateRangeChange = useCallback((values: [Dayjs | null, Dayjs | null] | null) => {
    setMediaFilters(prev => ({
      ...prev,
      from: values?.[0] ?? null,
      to: values?.[1] ?? null,
    }));
  }, []);

  // Effect for search filtering
  useEffect(() => {
    if (useMediaV2) {
      setImageState(prev => ({ ...prev, filteredLibrary: prev.library }));
      return;
    }
    const filtered = filterImages(imageState.searchTerm, imageState.library);
    setImageState(prev => ({ ...prev, filteredLibrary: filtered }));
  }, [useMediaV2, imageState.searchTerm, imageState.library, filterImages]);

  // Memoized folder tree
  const folderTreeData = useMemo(() => buildFolderTree(imageState.folders), [imageState.folders]);

  // Event handlers
  const openLibrary = useCallback(async () => {
    setVisibleLibraryCount(MEDIA_PAGE_SIZE);
    setPreviewImage(null);
    setMobileFoldersOpen(false);
    const folders = await loadFolders();
    let startFolder = options?.initialFolderId ?? null;
    if (options?.defaultFolderName) {
      let def = folders.find(f => f.name === options.defaultFolderName);
      if (!def) {
        try {
          const res = await conns<ImageFolder>('eshop:dashboard:image:folder:create').post({ name: options.defaultFolderName, parent_id: null });
          def = res.data;
          // 👇 Evita (ImageFolder | undefined)[]
          await loadFolders();
        } catch (error) {
          handleApiError(error, 'Error al crear carpeta');
        }
      }
      startFolder = def?.id ?? startFolder;
    }
    setImageState(prev => ({ ...prev, selectedFolder: startFolder }));
    await loadLibrary(startFolder);

    // Preselect current images and prioritize them only when opening the modal.
    const sel = buildSelectionPriorityIds(images, tempFiles);
    setImageState(prev => ({ ...prev, selected: new Set<string | number>(sel) }));
    setSelectionPriorityIds(sel);
    setLibVisible(true);
  }, [
    buildSelectionPriorityIds,
    loadFolders,
    loadLibrary,
    options?.initialFolderId,
    options?.defaultFolderName,
    images,
    tempFiles,
    handleApiError,
  ]);

  const filtersKey = useMemo(
    () =>
      [
        mediaFilters.kind,
        mediaFilters.from ? mediaFilters.from.toISOString() : '',
        mediaFilters.to ? mediaFilters.to.toISOString() : '',
        imageState.selectedFolder ?? 'root',
        imageState.includeDescendants ? '1' : '0',
      ].join('|'),
    [mediaFilters.kind, mediaFilters.from, mediaFilters.to, imageState.selectedFolder, imageState.includeDescendants],
  );

  useEffect(() => {
    setVisibleLibraryCount(MEDIA_PAGE_SIZE);
  }, [filtersKey]);

  const mediaKindSelectOptions = useMemo(
    () => [
      { value: 'images' as MediaKind, label: 'Imágenes' },
      { value: 'docs' as MediaKind, label: 'Documentos' },
      { value: 'favicon' as MediaKind, label: 'Favicons' },
      { value: 'other' as MediaKind, label: 'Otros' },
    ],
    [],
  );

  useEffect(() => {
    setVisibleLibraryCount(MEDIA_PAGE_SIZE);
    if (!libVisible || !useMediaV2) return;
    void fetchMediaList({
      reset: true,
      folderId: imageState.selectedFolder,
      cursor: null,
      includeDescendants: imageState.includeDescendants,
      filters: mediaFilters,
      searchTerm: imageState.searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, libVisible, useMediaV2, fetchMediaList]);

  useEffect(() => {
    setVisibleLibraryCount(MEDIA_PAGE_SIZE);
    if (!libVisible || !useMediaV2) return;
    if (previousSearchTermRef.current === imageState.searchTerm) {
      return;
    }
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }
    const timer = window.setTimeout(() => {
      fetchMediaList({
        reset: true,
        folderId: imageState.selectedFolder,
        cursor: null,
        includeDescendants: imageState.includeDescendants,
        filters: mediaFilters,
        searchTerm: imageState.searchTerm,
      });
      previousSearchTermRef.current = imageState.searchTerm;
    }, imageState.searchTerm ? 350 : 0);
    searchDebounceRef.current = timer;
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageState.searchTerm, libVisible, useMediaV2, fetchMediaList]);

  const toggleSelection = useCallback((id: string | number) => {
    setImageState(prev => {
      const targetSet = prev.organizeMode ? 'organizeSelected' : 'selected';
      let newSelected = new Set(prev[targetSet]);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        if (resolvedMode === 'single' && targetSet === 'selected') {
          newSelected = new Set([id]);
        } else if (!canAddSelection(newSelected.size, maxSelection)) {
          message.info(`Solo puedes seleccionar hasta ${maxSelection} imágenes.`);
          return prev;
        } else {
          newSelected.add(id);
        }
      }
      return { ...prev, [targetSet]: newSelected };
    });
  }, [maxSelection, resolvedMode]);

  const handleUpload = useCallback((_name: string, files: CustomUploadFile[]) => {
    const newFiles = files.filter(f => !tempFiles.some(tf => tf.uid === f.uid));

    setTempFiles(files);

    // Seleccionar automáticamente los nuevos archivos
    if (newFiles.length > 0) {
      setImageState(prev => {
        const targetSet = prev.organizeMode ? 'organizeSelected' : 'selected';
        let newSelected = new Set(prev[targetSet]);
        newFiles.forEach(f => {
          if (resolvedMode === 'single' && targetSet === 'selected') {
            newSelected = new Set([f.uid]);
          } else if (canAddSelection(newSelected.size, maxSelection)) {
            newSelected.add(f.uid);
          }
        });
        return { ...prev, [targetSet]: new Set(limitSelection(Array.from(newSelected), maxSelection)) };
      });
    }
  }, [maxSelection, tempFiles, resolvedMode]);

  const uploadNew = useCallback(async (files: CustomUploadFile[]): Promise<ImageInfo[]> => {
    if (!files.length) return [];

    const formData = buildImageUploadFormData(files, imageState.selectedFolder, {
      includeCaptions: true,
      useCaptionAsFilename: true,
    });
    if (!formData) {
      message.warning('No se detectaron archivos válidos para subir');
      return [];
    }

    try {
      const res = await conns<ImageInfo[]>('eshop:dashboard:image:upload').post(formData);
      return res.data;
    } catch (error) {
      handleApiError(error, 'Error al subir imágenes');
      return [];
    }
  }, [imageState.selectedFolder, handleApiError]);

  const refreshCaption = useCallback((imageId: number, caption: string | undefined) => {
    setImageState(prev => {
      const updateList = (list: ImageInfo[]) =>
        list.map(img => (img.id === imageId ? { ...img, caption } : img));
      return {
        ...prev,
        library: updateList(prev.library),
        filteredLibrary: updateList(prev.filteredLibrary),
      };
    });

    setImages(prev => {
      let changed = false;
      const next = prev.map(img => {
        if (img.id === imageId && img.caption !== caption) {
          changed = true;
          return { ...img, caption };
        }
        return img;
      });
      if (changed) {
        onChange(next);
        if (resolvedMode === 'single') {
          onSelect?.(next[0] ?? null);
        }
        return next;
      }
      return prev;
    });
  }, [onChange, resolvedMode, onSelect]);

  const updateImageCaption = useCallback(
    async (image: ImageInfo, rawCaption: string) => {
      if (!image.id) {
        message.warning('No es posible editar la etiqueta de un archivo temporal');
        return;
      }

      const trimmed = rawCaption.trim();
      try {
        const response = await conns<ImageInfo>('eshop:dashboard:image:update', [image.id]).put({
          caption: trimmed || null,
        });
        const updated = response.data;
        refreshCaption(image.id, updated.caption ?? undefined);
        message.success('Etiqueta actualizada correctamente');
      } catch (error) {
        handleApiError(error, 'Error al actualizar la etiqueta');
        throw error;
      }
    },
    [handleApiError, refreshCaption],
  );

  const resetUploadQueue = useCallback(() => {
    setTempFiles([]);
    setUploadSessionKey(prev => prev + 1);
  }, []);

  const accept = useCallback(async () => {
    if (imageState.organizeMode) {
      message.warning('Salí del modo organizar para subir o seleccionar.');
      return;
    }

    try {
      const newFiles = tempFiles.filter(f => imageState.selected.has(f.uid));
      const uploaded = await uploadNew(newFiles);

      if (uploaded.length) {
        setImageState(prev => {
          const nextLibrary = [...uploaded, ...prev.library];
          return {
            ...prev,
            library: nextLibrary,
            filteredLibrary: useMediaV2 ? nextLibrary : filterImages(prev.searchTerm, nextLibrary),
          };
        });
      }

      const existing = imageState.filteredLibrary.filter(img => img.id && imageState.selected.has(img.id));
      const combined = [...existing, ...uploaded];
      const limited = limitSelection(combined, maxSelection);
      const result = resolvedMode === 'multiple' ? limited : limited.slice(0, 1);

      setImages(result);
      onChange(result);
      if (resolvedMode === 'single') {
        onSelect?.(result[0] ?? null);
      }
      setLibVisible(false);
      setImageState(prev => ({ ...prev, selected: new Set(), organizeSelected: new Set() }));
      resetUploadQueue();

      message.success('Imágenes seleccionadas correctamente');
    } catch (error) {
      handleApiError(error, 'Error al procesar las imágenes');
    }
  }, [
    tempFiles,
    imageState.selected,
    imageState.filteredLibrary,
    uploadNew,
    onChange,
    handleApiError,
    resolvedMode,
    onSelect,
    resetUploadQueue,
    maxSelection,
  ]);

  // Folder management functions
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;

    try {
      const folderData = {
        name: newFolderName.trim(),
        parent_id: createParentId ?? null, // ← usar parent elegido (subcarpeta) o raíz
      };

      await conns('eshop:dashboard:image:folder:create').post(folderData);
      message.success('Carpeta creada exitosamente');
      setNewFolderName('');
      setFolderModalVisible(false);
      setEditingFolder(null);
      setCreateParentId(null); // reset
      await loadFolders();
    } catch (error) {
      handleApiError(error, 'Error al crear la carpeta');
    }
  }, [newFolderName, createParentId, loadFolders, handleApiError]);

  const handleUpdateFolder = useCallback(async () => {
    if (!editingFolder || !newFolderName.trim()) return;

    try {
      await conns(`eshop:dashboard:image:folder:update`, [editingFolder.id]).put({
        name: newFolderName.trim()
      });
      message.success('Carpeta actualizada exitosamente');
      setNewFolderName('');
      setFolderModalVisible(false);
      setEditingFolder(null);
      await loadFolders();
    } catch (error) {
      handleApiError(error, 'Error al actualizar la carpeta');
    }
  }, [editingFolder, newFolderName, loadFolders, handleApiError]);

  const handleDeleteFolder = useCallback(async (folder: ImageFolder) => {
    Modal.confirm({
      title: '¿Eliminar carpeta?',
      content: `¿Estás seguro de que deseas eliminar la carpeta "${folder.name}"? Las imágenes se moverán a la raíz.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await conns(`eshop:dashboard:image:folder:delete`, [folder.id]).delete();
          message.success('Carpeta eliminada exitosamente');
          await loadFolders();
          await loadLibrary();
        } catch (error) {
          handleApiError(error, 'Error al eliminar la carpeta');
        }
      }
    });
  }, [loadFolders, loadLibrary, handleApiError]);

  // Image moving functions - HABILITADA
  const moveSelectedImages = useCallback(async (targetFolderId: number | null) => {
    const selectedImages = imageState.filteredLibrary.filter(img => img.id && imageState.organizeSelected.has(img.id));
    if (selectedImages.length === 0) {
      message.warning('Selecciona al menos una imagen para mover');
      return;
    }

    try {
      // FIX: devolver la promesa en el map
      const movePromises = selectedImages.map(img =>
        img.id ? conns('eshop:dashboard:image:move', [img.id]).put({ folder_id: targetFolderId }) : Promise.resolve()
      );

      await Promise.all(movePromises);
      message.success(`${selectedImages.length} imagen(es) movida(s) exitosamente`);
      setImageState(prev => ({
        ...prev,
        organizeSelected: new Set(),
        selectedFolder: targetFolderId
      }));

      if (targetFolderId === null) {
        await loadLibrary(null);
      } else {
        await loadLibraryRecursive(targetFolderId);
      }
    } catch (error) {
      handleApiError(error, 'Error al mover las imágenes');
    }
  }, [imageState.filteredLibrary, imageState.organizeSelected, loadLibrary, loadLibraryRecursive, handleApiError]);

  // Drag and drop handlers
  const handleImageDragStart = useCallback((e: React.DragEvent, imageId: number) => {
    draggingImageRef.current = true;
    if (imageState.organizeMode && imageState.organizeSelected.has(imageId)) {
      const ids = Array.from(imageState.organizeSelected).filter((v): v is number => typeof v === 'number');
      if (ids.length > 1) {
        e.dataTransfer.setData('text/plain', `${DRAG_DATA_TYPES.IMAGES}${ids.join(',')}`);
      } else {
        e.dataTransfer.setData('text/plain', `${DRAG_DATA_TYPES.IMAGE}${imageId}`);
      }
    } else {
      e.dataTransfer.setData('text/plain', `${DRAG_DATA_TYPES.IMAGE}${imageId}`);
    }
    e.dataTransfer.effectAllowed = 'move';
  }, [imageState.organizeMode, imageState.organizeSelected]);

  const handleFolderDragOver = useCallback((e: React.DragEvent, folderId: number | null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setImageState(prev => ({ ...prev, dragOverFolder: folderId }));
  }, []);

  const handleFolderDragLeave = useCallback(() => {
    setImageState(prev => ({ ...prev, dragOverFolder: null }));
  }, []);

  const handleFolderDrop = useCallback(async (e: React.DragEvent, targetFolderId: number | null) => {
    e.preventDefault();
    setImageState(prev => ({ ...prev, dragOverFolder: null }));

    const data = e.dataTransfer.getData('text/plain');
    try {
      if (data.startsWith(DRAG_DATA_TYPES.IMAGES)) {
        const ids = data.replace(DRAG_DATA_TYPES.IMAGES, '').split(',').map(s => parseInt(s)).filter(n => !isNaN(n));
        if (ids.length) {
          await Promise.all(ids.map(id => conns('eshop:dashboard:image:move', [id]).put({ folder_id: targetFolderId })));
          message.success(`${ids.length} imagen(es) movida(s)`);
        }
      } else if (data.startsWith(DRAG_DATA_TYPES.IMAGE)) {
        const imageId = parseInt(data.replace(DRAG_DATA_TYPES.IMAGE, ''));
        if (!isNaN(imageId)) {
          await conns('eshop:dashboard:image:move', [imageId]).put({ folder_id: targetFolderId });
          message.success('Imagen movida');
        }
      }

      setImageState(prev => ({
        ...prev,
        selectedFolder: targetFolderId,
        organizeSelected: new Set()
      }));

      if (targetFolderId === null) {
        await loadLibrary(null);
      } else {
        await loadLibraryRecursive(targetFolderId);
      }
    } catch (error) {
      handleApiError(error, 'Error al mover la(s) imagen(es)');
    }
  }, [loadLibrary, loadLibraryRecursive, handleApiError]);

  // Folder tree drop handler
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFolderTreeDrop = useCallback(async (info: any) => {
    const { dragNode, node, dropPosition, dropToGap } = info;

    logInfo('Drop info:', { dragNode: dragNode.key, node: node.key, dropPosition, dropToGap });

    if (node.key === FOLDER_KEYS.ALL) {
      message.warning('No se puede mover carpetas a "Todas las imágenes"');
      return;
    }

    // Don't allow self-drop
    if (dragNode.key === node.key) return;

    // Prevent dropping parent into child
    const isDroppingIntoChild = (parentKey: string, childKey: string): boolean => {
      const checkNode = (nodeKey: string): boolean => {
        const folder = imageState.folders.find(f => f.id.toString() === nodeKey);
        if (!folder || !folder.parent_id) return false;
        if (folder.parent_id.toString() === parentKey) return true;
        return checkNode(folder.parent_id.toString());
      };
      return checkNode(childKey);
    };

    if (isDroppingIntoChild(dragNode.key, node.key)) {
      message.warning('No se puede mover una carpeta dentro de su propia subcarpeta');
      return;
    }

    try {
      let newParentId: number | null;

      if (!dropToGap) {
        // Dropped INTO a folder (becomes child)
        newParentId = parseInt(node.key);
      } else {
        newParentId = node.folder?.parent_id || null;
      }

      const draggedFolderId = parseInt(dragNode.key);
      const draggedFolder = imageState.folders.find(f => f.id === draggedFolderId);

      if (!draggedFolder) {
        message.error('Carpeta no encontrada');
        return;
      }

      // Update folder parent via API
      await conns(`eshop:dashboard:image:folder:update`, [draggedFolderId]).put({
        name: draggedFolder.name,
        parent_id: newParentId
      });

      message.success('Carpeta movida exitosamente');
      await loadFolders();
    } catch (error) {
      handleApiError(error, 'Error al mover la carpeta');
    }
  }, [imageState.folders, loadFolders, handleApiError]);

  // Toggle organize mode handler
  const toggleOrganizeMode = useCallback((enable: boolean) => {
    setImageState(prev => ({
      ...prev,
      organizeMode: enable,
      organizeSelected: new Set(), // Limpiar selección de organización
      // No tocar 'selected' para preservar selección de biblioteca
    }));
  }, []);

  // Delete selected images handler with FK confirmation
  const deleteSelectedImages = useCallback(async () => {
    const selectedIds = Array.from(imageState.organizeSelected).filter(
      (id): id is number => typeof id === 'number'
    );

    if (selectedIds.length === 0) {
      message.warning('Selecciona al menos una imagen para eliminar');
      return;
    }

    // Check FK references for all selected images
    let totalFkCount = 0;
    let fkImageCount = 0;

    try {
      for (const imageId of selectedIds) {
        const res = await conns<{ id: number; has_fk: boolean; item_count: number }>(
          'eshop:dashboard:image:delete_check',
          [imageId]
        ).get();
        if (res.data.item_count > 0) {
          totalFkCount += res.data.item_count;
          fkImageCount++;
        }
      }
    } catch (error) {
      handleApiError(error, 'Error al verificar referencias');
      return;
    }

    // Show confirmation modal via state
    setDeleteConfirmState({
      visible: true,
      imageIds: selectedIds,
      fkCount: totalFkCount,
      fkImageCount,
      isDeleting: false,
    });
  }, [imageState.organizeSelected, handleApiError]);

  // Execute the actual deletion
  const executeDeleteImages = useCallback(async () => {
    const { imageIds } = deleteConfirmState;
    if (imageIds.length === 0) return;

    setDeleteConfirmState(prev => ({ ...prev, isDeleting: true }));

    try {
      await Promise.all(
        imageIds.map(id => conns('eshop:dashboard:image:delete', [id]).delete())
      );
      message.success(`${imageIds.length} imagen(es) eliminada(s) correctamente`);
      setImageState(prev => ({
        ...prev,
        organizeSelected: new Set(),
      }));
      setDeleteConfirmState({ visible: false, imageIds: [], fkCount: 0, fkImageCount: 0, isDeleting: false });
      await loadLibrary(imageState.selectedFolder);
    } catch (error) {
      handleApiError(error, 'Error al eliminar imágenes');
      setDeleteConfirmState(prev => ({ ...prev, isDeleting: false }));
    }
  }, [deleteConfirmState, imageState.selectedFolder, loadLibrary, handleApiError]);

  const openCreateFolder = useCallback(() => {
    setEditingFolder(null);
    setCreateParentId(null);
    setNewFolderName('');
    setFolderModalVisible(true);
  }, []);

  // Memoized folder actions
  const folderActions = useMemo(() => [
    {
      key: 'create',
      label: 'Nueva carpeta',
      icon: <PlusOutlined />,
      onClick: openCreateFolder,
    },
  ], [openCreateFolder]);

  const mobileHeaderMenuItems = useMemo<MenuProps['items']>(() => {
    const items: MenuProps['items'] = [
      {
        key: 'create-folder',
        label: '+ Carpeta',
        icon: <PlusOutlined />,
      },
    ];

    if (resolvedMode === 'multiple') {
      items.push(
        { type: 'divider' },
        imageState.organizeMode
          ? { key: 'organize-off', label: 'Salir de organizar', icon: <MenuOutlined /> }
          : { key: 'organize-on', label: 'Organizar imágenes', icon: <DragOutlined /> },
      );
    }

    return items;
  }, [imageState.organizeMode, resolvedMode]);

  // Sync images with value prop
  useEffect(() => {
    setImages(resolvedMode === 'single' ? value.slice(0, 1) : value);
  }, [resolvedMode, value]);

  // Get current selection based on mode
  const getCurrentSelection = useCallback(() => {
    return imageState.organizeMode ? imageState.organizeSelected : imageState.selected;
  }, [imageState.organizeMode, imageState.organizeSelected, imageState.selected]);

  const gridItems = useMemo(() => {
    const prioritizedIds = prioritizeSelectedOnInteract
      ? getCurrentSelection()
      : new Set(selectionPriorityIds);
    const persistentItems = imageState.filteredLibrary
      .map(img => ({ img, id: img.id as number | string }))
      .sort((a, b) => {
        const aSelected = prioritizedIds.has(a.id);
        const bSelected = prioritizedIds.has(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
    const visiblePersistentItems = useMediaV2
      ? persistentItems
      : persistentItems.slice(0, visibleLibraryCount);

    return [
      ...visiblePersistentItems,
      ...tempFiles.map(f => ({ img: fileToInfo(f), id: f.uid })),
    ];
  }, [
    getCurrentSelection,
    imageState.filteredLibrary,
    prioritizeSelectedOnInteract,
    selectionPriorityIds,
    tempFiles,
    useMediaV2,
    visibleLibraryCount,
  ]);

  const canLoadMoreLibrary = useMediaV2
    ? imageState.hasMore
    : visibleLibraryCount < imageState.filteredLibrary.length;

  const currentFolderLabel = useMemo(() => {
    if (imageState.selectedFolder === null) return 'Todas las imágenes';
    return imageState.folders.find(folder => folder.id === imageState.selectedFolder)?.name ?? 'Carpeta seleccionada';
  }, [imageState.folders, imageState.selectedFolder]);

  const currentSelectionIds = useMemo(() => Array.from(getCurrentSelection()), [getCurrentSelection]);
  const primarySelectionId = currentSelectionIds[0] ?? null;

  const pendingSelectionImage = useMemo(() => {
    if (primarySelectionId == null) return null;
    if (typeof primarySelectionId === 'number') {
      return (
        imageState.library.find(img => img.id === primarySelectionId) ??
        images.find(img => img.id === primarySelectionId) ??
        null
      );
    }

    const tempMatch = tempFiles.find(file => file.uid === primarySelectionId);
    return tempMatch ? fileToInfo(tempMatch) : null;
  }, [primarySelectionId, imageState.library, images, tempFiles]);

  const selectedImage = useMemo(() => (resolvedMode === 'single' ? images[0] ?? null : null), [images, resolvedMode]);
  const visibleSelectedImages = useMemo(
    () => images.slice(0, selectedPreviewPage * SELECTED_PREVIEW_PAGE_SIZE),
    [images, selectedPreviewPage],
  );
  const canShowMoreSelectedImages = visibleSelectedImages.length < images.length;

  return (
    <div
      onKeyDown={stopParentSubmitOnEnter}
      onSubmitCapture={stopSubmitCapture}
    >
      <div className="w-100 text-start">
        <Space align="start" wrap size={16}>
          <Button
            size={triggerSize}
            type={triggerType}
            icon={triggerIcon}
            aria-label={triggerAriaLabel ?? triggerText}
            onClick={openLibrary}
            className="me-auto"
          >
            {triggerText}
          </Button>
          {resolvedMode === 'single' && showPreview && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              {selectedImage ? (
                <>
                  <div
                    data-testid="image-manager-preview-inline"
                    className="image-manager-checkered"
                    style={{
                      width: previewDimensions.w,
                      height: previewDimensions.h,
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Abrir gestor de imágenes"
                    onClick={() => void openLibrary()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        void openLibrary();
                      }
                    }}
                  >
                    {isImageKind(selectedImage.kind) ? (
                      <Image
                        src={getThumbUrl(selectedImage)}
                        alt={resolveAltText(
                          selectedImage,
                          getMediaLabel(
                            selectedImage,
                            selectedImage.id ?? selectedImage.file_path_large ?? 'preview'
                          )
                        )}
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'scale-down' }}
                        preview={false}
                      />
                    ) : (
                      renderNonImagePreview(
                        selectedImage,
                        getMediaLabel(
                          selectedImage,
                          selectedImage.id ?? selectedImage.file_path_large ?? 'preview'
                        ),
                        'preview',
                      )
                    )}
                  </div>
                  <div style={{ minWidth: 160, maxWidth: 240 }}>
                    <Typography.Text
                      strong
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      Previsualización
                    </Typography.Text>
                    <Typography.Text
                      type={selectedImage.caption?.trim() ? undefined : 'secondary'}
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.5 }}
                    >
                      {selectedImage.caption?.trim() || 'Sin caption'}
                    </Typography.Text>
                  </div>
                </>
              ) : (
                ''
              )}
            </div>
          )}
        </Space>
      </div>

      {/* Sección de imágenes seleccionadas */}
      {showPreview && resolvedMode === 'multiple' && images.length > 0 && (
        <div
          className="mt-3"
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '12px',
            backgroundColor: '#fafafa11',
          }}
        >
          <div
            style={{
              marginBottom: '8px',
              fontSize: '12px',
              color: '#666',
              fontWeight: 500,
            }}
          >
            Imágenes seleccionadas ({images.length})
          </div>
          <Row gutter={[12, 12]}>
            {visibleSelectedImages.map((img, index) => {
              const imageKey = getImageKey(img, index);
              return (
                <Col key={img.id || index} xs={12} sm={8} md={6} lg={4} xl={3}>
                  <div
                    className="image-manager-checkered"
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9',
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Abrir gestor de imágenes"
                    onClick={() => void openLibrary()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        void openLibrary();
                      }
                    }}
                  >
                    <Image
                      src={getThumbUrl(img)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'scale-down',
                      }}
                      preview={{
                        src: getThumbUrl(img, 'large'),
                      }}
                      alt={resolveAltText(img, `Imagen ${index + 1}`)}
                    />
                    {img.caption && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '4px',
                          fontSize: '10px',
                          textAlign: 'center',
                        }}
                      >
                        {img.caption}
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text
                      type={img.caption?.trim() ? undefined : 'secondary'}
                      style={{
                        display: 'block',
                        fontSize: 12,
                        marginBottom: 4,
                      }}
                    >
                      {img.caption?.trim() || 'Sin caption'}
                    </Typography.Text>
                    {!captionEditorOpen.has(imageKey) ? (
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        aria-label="Editar caption"
                        onClick={() => openCaptionEditor(imageKey, img.caption)}
                        style={{ paddingInline: 0 }}
                      >
                        Editar caption
                      </Button>
                    ) : (
                      <Space
                        direction="vertical"
                        size={6}
                        style={{ width: '100%' }}
                      >
                        <Input.TextArea
                          autoSize={{ minRows: 1, maxRows: 2 }}
                          maxLength={125}
                          showCount
                          aria-label="Caption"
                          placeholder="Escribí un caption..."
                          value={captionEditorDrafts[imageKey] ?? ''}
                          onChange={(event) =>
                            handleCaptionDraftChange(
                              imageKey,
                              event.target.value
                            )
                          }
                        />
                        <Space size={4}>
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => confirmCaptionChange(imageKey)}
                          >
                            Confirmar
                          </Button>
                          <Button
                            size="small"
                            onClick={() => closeCaptionEditor(imageKey)}
                          >
                            Cancelar
                          </Button>
                        </Space>
                      </Space>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
          {canShowMoreSelectedImages && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => setSelectedPreviewPage((prev) => prev + 1)}
              >
                Ver más
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modal principal de gestión de imágenes */}
      <Modal
        open={libVisible}
        onOk={accept}
        onCancel={() => {
          setLibVisible(false);
          setPreviewImage(null);
          setMobileFoldersOpen(false);
          resetUploadQueue();
          setImageState((prev) => ({
            ...prev,
            selected: new Set(),
            organizeSelected: new Set(),
            searchTerm: '',
            selectedFolder: null,
            organizeMode: false,
          }));
          previousSearchTermRef.current = '';
        }}
        width={isMobile ? 'calc(100vw - 16px)' : 1200}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {isMobile && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: mobileHeaderMenuItems,
                  onClick: ({ key }) => {
                    if (key === 'create-folder') {
                      openCreateFolder();
                    } else if (key === 'organize-on') {
                      toggleOrganizeMode(true);
                    } else if (key === 'organize-off') {
                      toggleOrganizeMode(false);
                    }
                  },
                }}
              >
                <Button
                  size="small"
                  type={imageState.organizeMode ? 'primary' : 'default'}
                  icon={<MenuOutlined />}
                  aria-label="Abrir menú de imágenes"
                />
              </Dropdown>
            )}
            <span>Gestionar imágenes</span>
            {imageState.organizeMode && (
              <Badge
                count="Modo Organizar"
                style={{ backgroundColor: '#52c41a' }}
              />
            )}
          </div>
        }
        okText="Seleccionar"
        cancelText="Cancelar"
        // ✅ en lugar de ocultar el footer, deshabilitamos el OK si corresponde
        okButtonProps={{
          htmlType: 'button',
          disabled:
            imageState.organizeMode ||
            (tempFiles.length === 0 && imageState.selected.size === 0),
        }}
        // footer={imageState.organizeMode ? null : undefined}
        cancelButtonProps={{ htmlType: 'button' }}
        styles={{
          body: {
            maxHeight: isMobile ? 'calc(100vh - 132px)' : '72vh',
            overflowY: 'auto',
          },
          footer: {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            marginTop: 0,
            paddingTop: 12,
            borderTop: '1px solid var(--ant-color-border-secondary, rgba(5, 5, 5, 0.06))',
          },
        }}
        /* ⬇️ Envolver contenido del portal */
        modalRender={(node) => (
          <div
            onKeyDown={stopParentSubmitOnEnter}
            onSubmitCapture={(e) => e.preventDefault()}
          >
            {node}
          </div>
        )}
      >
        <Row gutter={isMobile ? [8, 8] : 16} style={{ minHeight: isMobile ? 0 : '500px', width: '100%' }}>
          {/* Panel lateral de carpetas */}
          <Col
            xs={24}
            sm={6}
            style={{ borderRight: isMobile ? 'none' : '1px solid #f0f0f0' }}
          >
            {isMobile && (
              <Button
                type="text"
                block
                className="image-manager-mobile-folder-trigger"
                aria-expanded={mobileFoldersOpen}
                aria-label="Seleccionar carpeta"
                onClick={() => setMobileFoldersOpen(prev => !prev)}
              >
                <span className="image-manager-mobile-folder-label">{currentFolderLabel}</span>
                <DownOutlined rotate={mobileFoldersOpen ? 180 : 0} />
              </Button>
            )}

            {!isMobile && <div style={{ marginBottom: '16px' }}>
              <Dropdown menu={{ items: folderActions }} trigger={['click']}>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  aria-label="Gestionar carpetas"
                >
                  Carpetas
                </Button>
              </Dropdown>
            </div>}

            <div
              className="image-manager-mobile-folder-tree"
              style={{
                display: !isMobile || mobileFoldersOpen ? 'block' : 'none',
                marginTop: isMobile ? 4 : undefined,
              }}
            >
              {(!isMobile || mobileFoldersOpen) && (
                <Tree
                  className="tree-inline-actions"
                  showIcon={false}
                  draggable
                  blockNode
                  treeData={folderTreeData}
                  onDrop={handleFolderTreeDrop}
                  selectedKeys={[
                imageState.selectedFolder !== null
                  ? String(imageState.selectedFolder)
                  : FOLDER_KEYS.ALL,
              ]}
              onSelect={(selectedKeys) => {
                const key = selectedKeys[0];
                if (key === FOLDER_KEYS.ALL) {
                  setImageState((prev) => ({ ...prev, selectedFolder: null }));
                  loadLibrary();
                } else if (key) {
                  const folderId = parseInt(key as string);
                  setImageState((prev) => ({
                    ...prev,
                    selectedFolder: folderId,
                  }));
                  loadLibraryRecursive(folderId);
                }
                if (isMobile) setMobileFoldersOpen(false);
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              titleRender={(node: any) => {
                const items: MenuProps['items'] = [
                  {
                    key: 'createSub',
                    label: (
                      <span>
                        <PlusOutlined style={{ marginRight: 8 }} />
                        Subcarpeta
                      </span>
                    ),
                  },
                  {
                    key: 'edit',
                    label: (
                      <span>
                        <EditOutlined style={{ marginRight: 8 }} />
                        Editar
                      </span>
                    ),
                  },
                  { type: 'divider' },
                  {
                    key: 'delete',
                    danger: true,
                    label: (
                      <span>
                        <DeleteOutlined style={{ marginRight: 8 }} />
                        Eliminar
                      </span>
                    ),
                  },
                ];

                // Agregar acción de mover imágenes en modo organizar
                if (
                  imageState.organizeMode &&
                  imageState.organizeSelected.size > 0
                ) {
                  items.unshift(
                    {
                      key: 'moveHere',
                      label: (
                        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                          <DragOutlined style={{ marginRight: 8 }} />
                          Mover aquí ({imageState.organizeSelected.size})
                        </span>
                      ),
                    },
                    { type: 'divider' }
                  );
                }

                const onMenuClick: MenuProps['onClick'] = ({ key }) => {
                  if (key === 'moveHere') {
                    const folderId =
                      node.key === 'all' ? null : parseInt(node.key);
                    moveSelectedImages(folderId);
                  } else if (key === 'createSub') {
                    setEditingFolder(null);
                    setCreateParentId(node.folder?.id ?? null); // ← subcarpeta de esta
                    setNewFolderName('');
                    setFolderModalVisible(true);
                  } else if (key === 'edit') {
                    setEditingFolder(node.folder);
                    setNewFolderName(node.folder.name);
                    setFolderModalVisible(true);
                  } else if (key === 'delete') {
                    Modal.confirm({
                      title: 'Eliminar carpeta',
                      content: `¿Seguro que querés eliminar "${node.folder?.name}"?`,
                      okText: 'Eliminar',
                      cancelText: 'Cancelar',
                      okButtonProps: { danger: true },
                      onOk: () => handleDeleteFolder(node.folder),
                    });
                  }
                };

                const folderId = node.key === 'all' ? -1 : parseInt(node.key);
                const isDragOver = imageState.dragOverFolder === folderId;

                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      width: '100%',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      /* TODO: PROBAR */
                      // backgroundColor: isDragOver ? '#e6f7ff' : 'transparent',
                      border: isDragOver
                        ? '2px dashed #1890ff'
                        : '2px solid transparent',
                    }}
                    onDragOver={(e) => handleFolderDragOver(e, folderId)}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={(e) => handleFolderDrop(e, folderId)}
                  >
                    {/* Izquierda: ícono + nombre con ellipsis */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {node.icon ??
                        (node.key === 'all' ? (
                          <AppstoreOutlined />
                        ) : (
                          <FolderOutlined />
                        ))}
                      <Tooltip title={String(node.title)}>
                        <Typography.Text
                          className="text-ellipsis-1"
                          style={{
                            flex: 1,
                            color: 'var(--text-color-default, black)',
                          }}
                        >
                          {node.title}
                        </Typography.Text>
                      </Tooltip>
                    </div>

                    {/* Derecha: menú de 3 puntos (no para 'all') */}
                    {node.key !== 'all' && (
                      <span
                        onClick={(e) => e.stopPropagation()}
                        style={{ flexShrink: 0 }}
                      >
                        <Dropdown
                          trigger={['click']}
                          placement="bottomRight"
                          menu={{ items, onClick: onMenuClick }}
                        >
                          <Button
                            aria-label="Más acciones"
                            size="small"
                            type="text"
                            icon={<MoreOutlined />}
                          />
                        </Dropdown>
                      </span>
                    )}
                  </div>
                );
              }}
                />
              )}
            </div>
          </Col>

          {/* Panel principal de contenido */}
          <Col
            xs={24}
            sm={resolvedMode === 'single' ? 16 : 18}
            md={resolvedMode === 'single' ? 12 : 18}
          >
            {/* Barra de herramientas */}
            <div style={{ marginBottom: '16px' }}>
              <Row gutter={[16, 12]} align="middle" wrap>
                <Col flex="320px">
                  <Input
                    placeholder="Buscar por caption..."
                    prefix={<SearchOutlined />}
                    value={imageState.searchTerm}
                    onChange={(e) =>
                      setImageState((prev) => ({
                        ...prev,
                        searchTerm: e.target.value,
                      }))
                    }
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Col>
                {useMediaV2 && (
                  <Col flex="auto">
                    <Space size={12} wrap>
                      <Tag color="blue">Sitio: {resolvedSiteCode}</Tag>
                      <Select<MediaKind>
                        aria-label="Filtrar por tipo"
                        size="small"
                        value={mediaFilters.kind}
                        onChange={handleKindChange}
                        options={mediaKindSelectOptions}
                        style={{ minWidth: 160 }}
                      />
                      <RangePicker
                        aria-label="Filtrar por fecha"
                        size="small"
                        value={[mediaFilters.from, mediaFilters.to]}
                        onChange={handleDateRangeChange}
                        allowClear
                        format="YYYY-MM-DD"
                        style={{ minWidth: 220 }}
                        placeholder={['Desde', 'Hasta']}
                      />
                    </Space>
                  </Col>
                )}
                {/*<Col>*/}
                  {/*<Space>*/}
                    {imageState.organizeMode &&
                      imageState.organizeSelected.size > 0 && (
                        <Col>
                          <Space>
                            <Badge
                              count={imageState.organizeSelected.size}
                              showZero
                            >
                              <Button
                                size="small"
                                type="primary"
                                ghost
                                onClick={() =>
                                  setImageState((prev) => ({
                                    ...prev,
                                    organizeSelected: new Set(),
                                  }))
                                }
                              >
                                Deseleccionar
                              </Button>
                            </Badge>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={deleteSelectedImages}
                            >
                              Eliminar
                            </Button>
                          </Space>
                        </Col>
                      )}
                    {resolvedMode === 'multiple' && !isMobile && (
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            !imageState.organizeMode
                              ? {
                                  key: 'organize_on',
                                  label: 'Organizar imágenes',
                                  icon: <DragOutlined />,
                                }
                              : {
                                  key: 'organize_off',
                                  label: 'Salir de organizar',
                                  icon: <MenuOutlined />,
                                },
                          ],
                          onClick: ({ key }) => {
                            if (key === 'organize_on') {
                              toggleOrganizeMode(true);
                            } else if (key === 'organize_off') {
                              toggleOrganizeMode(false);
                            }
                          },
                        }}
                      >
                        <Button
                          size="small"
                          icon={<MenuOutlined />}
                          aria-label={imageState.organizeMode ? 'Salir de organizar' : 'Organizar imágenes'}
                          type={imageState.organizeMode ? 'primary' : 'default'}
                        />
                      </Dropdown>
                    )}
                  {/*</Space>*/}
                {/*</Col>*/}
              </Row>
            </div>

            <Divider style={{ margin: isMobile ? '8px 0' : '16px 0' }} />

            {/* Componente de subida de archivos (oculto en modo organizar) */}
            {!imageState.organizeMode ? (
              <>
                <UploadDragerSorteable
                  key={uploadSessionKey}
                  className="image-manager-upload"
                  name="uploads"
                  handleChange={handleUpload}
                  value={tempFiles}
                />
                <Divider style={{ margin: isMobile ? '8px 0' : '16px 0' }} />
              </>
            ) : (
              <>
                <div
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #52c41a',
                    borderRadius: 6,
                    fontSize: 13,
                    color: '#389e0d',
                    backgroundColor: '#f6ffed',
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    🎯 Modo Organizar Activado
                  </div>
                  <div style={{ fontSize: 12 }}>
                    • Seleccioná imágenes con los checkboxes
                    <br />
                    • Arrastralas a carpetas del panel izquierdo
                    <br />• O usá el menú de carpetas {'"Mover aquí"'}
                  </div>
                </div>
                <Divider style={{ margin: isMobile ? '8px 0' : '16px 0' }} />
              </>
            )}

            {/* Grid de imágenes con drag & drop */}
            {useMediaV2 &&
            imageState.isLoading &&
            imageState.filteredLibrary.length === 0 &&
            tempFiles.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px 0',
                }}
              >
                <Spin size="large" />
              </div>
            ) : gridItems.length > 0 ? (
              <>
                <Row gutter={[8, 8]}>
                  {gridItems.map(({ img, id }) => {
                      const currentSelection = getCurrentSelection();
                      const isSelected = currentSelection.has(id);
                      const label = getMediaLabel(img, id);

                      return (
                        <Col key={id} xs={12} sm={8} md={6}>
                          <div
                            style={{
                              textAlign: 'center',
                              position: 'relative',
                              cursor: 'pointer',
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`${isSelected ? 'Deseleccionar' : 'Seleccionar'} ${label}`}
                            aria-pressed={isSelected}
                            onClick={() => {
                              if (!draggingImageRef.current) toggleSelection(id);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleSelection(id);
                              }
                            }}
                            draggable={
                              typeof id === 'number' && imageState.organizeMode
                            }
                            onDragStart={(e) => {
                              if (
                                typeof id === 'number' &&
                                imageState.organizeMode
                              ) {
                                handleImageDragStart(e, id);
                              }
                            }}
                            onDragEnd={() => {
                              window.setTimeout(() => {
                                draggingImageRef.current = false;
                              }, 0);
                            }}
                          >
                            {(multiple || imageState.organizeMode) && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() => toggleSelection(id)}
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  left: '8px',
                                  zIndex: 10,
                                }}
                              />
                            )}

                            {typeof id === 'number' && (
                              <Space
                                size={4}
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  zIndex: 10,
                                }}
                              >
                                {isImageKind(img.kind) && (
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<EyeOutlined />}
                                    aria-label={`Vista previa de ${label}`}
                                    style={{
                                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                      color: '#fff',
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setPreviewImage(img);
                                    }}
                                  />
                                )}
                                {!imageState.organizeMode && (
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<EditOutlined />}
                                  aria-label={`Editar etiqueta de ${label}`}
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                    color: '#fff',
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setCaptionEditor({
                                      image: img,
                                      value: img.caption ?? '',
                                    });
                                  }}
                                />
                                )}
                                {imageState.organizeMode && (
                                  <DragOutlined
                                    aria-label={`Arrastrar ${label}`}
                                    style={{
                                      color: isSelected ? '#1890ff' : '#666',
                                      backgroundColor: 'rgba(255,255,255,0.9)',
                                      padding: '4px',
                                      borderRadius: '2px',
                                      fontSize: '12px',
                                    }}
                                  />
                                )}
                              </Space>
                            )}
                            <div
                              className="image-manager-checkered"
                              style={{
                                position: 'relative',
                                aspectRatio: '1',
                                overflow: 'hidden',
                                borderRadius: '4px',
                                border: isSelected
                                  ? '2px solid #1890ff'
                                  : '1px solid #d9d9d9',
                                opacity:
                                  imageState.organizeMode && !isSelected
                                    ? 0.7
                                    : 1,
                                transform:
                                  isSelected && imageState.organizeMode
                                    ? 'scale(0.98)'
                                    : 'scale(1)',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {isImageKind(img.kind) ? (
                                <Image
                                  src={getThumbUrl(img)}
                                  width="100%"
                                  height="100%"
                                  style={{ objectFit: 'scale-down' }}
                                  preview={false}
                                  alt={resolveAltText(img, label)}
                                />
                              ) : (
                                renderNonImagePreview(img, label, 'grid')
                              )}
                              {img.caption && isImageKind(img.kind) && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    padding: '2px 4px',
                                    fontSize: '10px',
                                    textAlign: 'center',
                                  }}
                                >
                                  {img.caption}
                                </div>
                              )}
                            </div>
                            {typeof id !== 'number' && (
                              <Input
                                size="small"
                                allowClear
                                aria-label="Agregar etiqueta"
                                placeholder="Agregar etiqueta"
                                value={
                                  tempFileMap.get(id as string)?.caption ?? ''
                                }
                                onChange={(event) =>
                                  handleTempCaptionChange(
                                    id as string,
                                    event.target.value
                                  )
                                }
                                onClick={(event) => event.stopPropagation()}
                                onMouseDown={(event) => event.stopPropagation()}
                                onKeyDown={(event) => event.stopPropagation()}
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </div>
                        </Col>
                      );
                    })}
                </Row>

                <Image.PreviewGroup
                  items={
                    previewImage
                      ? [{
                          src: getThumbUrl(previewImage, 'large'),
                          alt: resolveAltText(
                            previewImage,
                            getMediaLabel(
                              previewImage,
                              previewImage.id ?? previewImage.file_path_large ?? 'preview',
                            ),
                          ),
                        }]
                      : []
                  }
                  preview={{
                    open: Boolean(previewImage),
                    onOpenChange: (open) => {
                      if (!open) setPreviewImage(null);
                    },
                  }}
                />

                {canLoadMoreLibrary && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Button
                      onClick={loadMore}
                      loading={imageState.isLoading}
                      disabled={imageState.isLoading}
                    >
                      Mostrar 20 más
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Empty
                description={
                  tempFiles.length > 0
                    ? 'Aún no hay coincidencias con la búsqueda'
                    : 'No hay elementos en esta carpeta'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Col>

          {resolvedMode === 'single' && (
            <Col
              xs={24}
              sm={8}
              md={6}
              style={{ borderLeft: '1px solid #f0f0f0' }}
            >
              <div style={{ paddingLeft: 16, paddingTop: 8 }}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Vista previa
                </Typography.Title>
                <div
                  data-testid="image-manager-preview-modal"
                  className="image-manager-checkered"
                  style={{
                    width: previewDimensions.w,
                    maxWidth: '100%',
                    height: previewDimensions.h,
                    borderRadius: 6,
                    border: '1px solid #d9d9d9',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  {pendingSelectionImage ? (
                    isImageKind(pendingSelectionImage.kind) ? (
                      <Image
                        src={getThumbUrl(pendingSelectionImage)}
                        alt={resolveAltText(
                          pendingSelectionImage,
                          getMediaLabel(
                            pendingSelectionImage,
                            pendingSelectionImage.id ??
                              pendingSelectionImage.file_path_large ??
                              'preview'
                          )
                        )}
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'scale-down' }}
                        preview={false}
                      />
                    ) : (
                      renderNonImagePreview(
                        pendingSelectionImage,
                        getMediaLabel(
                          pendingSelectionImage,
                          pendingSelectionImage.id ??
                            pendingSelectionImage.file_path_large ??
                            'preview'
                        ),
                        'preview'
                      )
                    )
                  ) : (
                    <Empty
                      description="Sin selección"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                  {pendingSelectionImage ? (
                    <Typography.Text
                      type={pendingSelectionImage.caption?.trim() ? undefined : 'secondary'}
                    >
                      {pendingSelectionImage.caption?.trim() || 'Sin caption'}
                    </Typography.Text>
                  ) : (
                    <div>Elegí un elemento para revisar el preview.</div>
                  )}
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Modal>

      {/* Modal para crear/editar carpetas */}
      <Modal
        title={editingFolder ? 'Editar carpeta' : 'Nueva carpeta'}
        open={folderModalVisible}
        onOk={editingFolder ? handleUpdateFolder : handleCreateFolder}
        onCancel={() => {
          setFolderModalVisible(false);
          setEditingFolder(null);
          setCreateParentId(null);
          setNewFolderName('');
        }}
        okText={editingFolder ? 'Actualizar' : 'Crear'}
        cancelText="Cancelar"
      >
        <Input
          placeholder="Nombre de la carpeta"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={editingFolder ? handleUpdateFolder : handleCreateFolder}
        />
      </Modal>

      <Modal
        title="Editar etiqueta"
        open={!!captionEditor}
        onCancel={() => {
          if (savingCaption) return;
          setCaptionEditor(null);
        }}
        onOk={async () => {
          if (!captionEditor) return;
          setSavingCaption(true);
          try {
            await updateImageCaption(captionEditor.image, captionEditor.value);
            setCaptionEditor(null);
          } finally {
            setSavingCaption(false);
          }
        }}
        confirmLoading={savingCaption}
        okButtonProps={{ disabled: !captionEditor }}
        destroyOnHidden
      >
        <Input
          placeholder="Etiqueta"
          maxLength={200}
          value={captionEditor?.value ?? ''}
          onChange={(event) => {
            const { value: nextValue } = event.target;
            setCaptionEditor((prev) =>
              prev ? { ...prev, value: nextValue } : prev
            );
          }}
          onPressEnter={async (event) => {
            event.preventDefault();
            if (!captionEditor || savingCaption) {
              return;
            }
            setSavingCaption(true);
            try {
              await updateImageCaption(
                captionEditor.image,
                captionEditor.value
              );
              setCaptionEditor(null);
            } finally {
              setSavingCaption(false);
            }
          }}
        />
        <Typography.Paragraph style={{ marginTop: 8 }} type="secondary">
          Podés dejar la etiqueta vacía para eliminarla. Máximo 200 caracteres.
        </Typography.Paragraph>
      </Modal>

      {/* Modal de confirmación de eliminación de imágenes */}
      <Modal
        title={
          deleteConfirmState.fkCount > 0
            ? '⚠️ Imágenes vinculadas a elementos'
            : 'Eliminar imágenes'
        }
        open={deleteConfirmState.visible}
        onOk={executeDeleteImages}
        onCancel={() =>
          setDeleteConfirmState((prev) => ({ ...prev, visible: false }))
        }
        okText="Eliminar"
        okType="danger"
        cancelText="Cancelar"
        confirmLoading={deleteConfirmState.isDeleting}
        okButtonProps={{ disabled: deleteConfirmState.isDeleting }}
        cancelButtonProps={{ disabled: deleteConfirmState.isDeleting }}
      >
        <p>
          {deleteConfirmState.fkCount > 0
            ? `${deleteConfirmState.imageIds.length} imagen(es) seleccionada(s). ${deleteConfirmState.fkImageCount} imagen(es) están vinculadas a ${deleteConfirmState.fkCount} elemento(s) del catálogo. ¿Desvinculamos y eliminamos?`
            : `¿Eliminar ${deleteConfirmState.imageIds.length} imagen(es) seleccionada(s)?`}
        </p>
      </Modal>
    </div>
  );
};

export default ImageManager;
